import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, ClipboardList, Warehouse, Wrench, ShieldCheck, Building2, Briefcase, Globe, TrendingUp, Droplets, HardHat, Hammer, Ruler } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { localized, localizedList } from '../hooks/useServices';

export const serviceIcons = { ClipboardList, Warehouse, Wrench, ShieldCheck, Building2, Briefcase, Globe, TrendingUp, Droplets, HardHat, Hammer, Ruler };

export const ServiceIcon = ({ name, className }) => {
  const Icon = serviceIcons[name] || Building2;
  return <Icon className={className} aria-hidden="true" />;
};

export const servicePath = (service) => `/leistungen/${service.slug || service.key}`;

export function ServiceCard({ service, index = 0 }) {
  const { language, t, lp } = useLanguage();
  const title = localized(service, 'title', language);
  const featured = !!service.featured;

  return (
    <article
      data-testid={`service-card-${service.key}`}
      className={`group relative flex flex-col bg-white rounded-2xl p-7 lg:p-8 animate-fadeInUp transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 ${
        featured ? 'border-2 border-[#066ce7] shadow-[0_24px_60px_-28px_rgba(6,108,231,0.55)]' : 'border border-slate-200 shadow-sm hover:border-[#066ce7]/60 hover:shadow-[0_24px_60px_-30px_rgba(6,108,231,0.45)]'
      }`}
      style={{ animationDelay: `${0.08 * index}s`, opacity: 0, animationFillMode: 'forwards' }}
    >
      <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-colors duration-300 ${
        featured ? 'bg-[#066ce7] text-white' : 'bg-blue-50 text-[#066ce7] group-hover:bg-[#066ce7] group-hover:text-white'
      }`}>
        <ServiceIcon name={service.icon} className="w-7 h-7" />
      </div>
      <h3 className="text-2xl font-bold text-[#0F172A] mb-1.5">{title}</h3>
      <p className="text-[#066ce7] font-semibold mb-4 leading-snug">{localized(service, 'subtitle', language)}</p>
      <p className="text-slate-600 text-[15px] leading-relaxed mb-6">{localized(service, 'description', language)}</p>
      <ul className="space-y-2 mb-8">
        {localizedList(service, 'bullets', language).map((b, i) => (
          <li key={i} className="flex items-start gap-3 rounded-lg bg-slate-50 px-3 py-2.5 text-sm text-slate-700 transition-colors duration-300 group-hover:bg-blue-50/70">
            <ArrowRight className="w-4 h-4 text-[#066ce7] mt-0.5 shrink-0" strokeWidth={2.5} aria-hidden="true" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <Link
        to={lp(servicePath(service))}
        className="mt-auto inline-flex items-center gap-1 text-[#066ce7] font-semibold transition-[gap] duration-200 hover:gap-2"
        aria-label={`${t('services.more')}: ${title}`}
        data-testid={`service-more-${service.key}`}
      >
        {t('services.more')} <ChevronRight className="w-4 h-4" aria-hidden="true" />
      </Link>
    </article>
  );
}

export function ServiceGrid({ services }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-4 gap-6 items-stretch" data-testid="service-grid">
      {services.map((s, i) => <ServiceCard key={s.id || s.key} service={s} index={i} />)}
    </div>
  );
}
